import { createContext, useCallback, useContext } from 'react';
import { BLOG_CDN_BASE, resolveAsset } from './cdn';

/**
 * Carries the current blog's slug (and asset base) down to MDX components so
 * relative asset paths (`images/foo.jpg`) can resolve to
 * `<base>/<slug>/images/foo.jpg` without the author ever writing an absolute
 * URL. `assetBase` is the CDN in production; the local preview harness
 * (`/preview/:slug`) overrides it to load assets from `public/`.
 */
export interface BlogContextValue {
  slug: string;
  assetBase: string;
}

export const BlogContext = createContext<BlogContextValue>({ slug: '', assetBase: BLOG_CDN_BASE });

export const useBlogSlug = (): string => useContext(BlogContext).slug;

/**
 * Returns a resolver bound to the current blog's slug + asset base. Every MDX
 * component that loads an asset should go through this so the preview harness's
 * base override applies uniformly.
 */
export const useResolveAsset = (): ((src: string) => string) => {
  const { slug, assetBase } = useContext(BlogContext);
  return useCallback((src: string) => resolveAsset(slug, src, assetBase), [slug, assetBase]);
};
